// Organization (Account) Create Page — real create form.
// POST /api/v1/organizations { name, domain?, contactEmail? } → created org row
// (with id). On success: navigate to /organizations/:id + success toast.
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { apiRequest } from '@/lib/supabase';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { useToast } from '@/components/shared/ToastProvider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

interface CreatedOrg {
  id: string;
  name: string;
}

export function OrganizationCreatePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = name.trim().length > 0 && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      // Only send fields the backend's createOrgSchema accepts. Empty optional
      // fields are omitted (the schema would reject an empty-string email).
      const body: Record<string, string> = { name: name.trim() };
      if (domain.trim()) body.domain = domain.trim();
      if (contactEmail.trim()) body.contactEmail = contactEmail.trim();

      const created = await apiRequest<CreatedOrg>('/organizations', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      toast({ kind: 'success', title: 'Account created.', body: created.name });
      navigate(`/organizations/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create account');
      setSubmitting(false);
    }
  };

  const fieldWrap: React.CSSProperties = { marginBottom: '18px' };
  const helpStyle: React.CSSProperties = { fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' };

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Link to="/organizations" style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }} aria-label="Back to accounts">
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h1 className="page-title">New Account</h1>
            <p className="page-subtitle">Create a customer organization</p>
          </div>
        </div>
      </div>

      <Breadcrumb crumbs={[{ label: 'Accounts', to: '/organizations' }, { label: 'New' }]} />

      <form onSubmit={handleSubmit} className="card" style={{ maxWidth: '560px', padding: '24px' }}>
        {error && (
          <div style={{ padding: '10px 12px', background: '#FEE2E2', borderRadius: '6px', marginBottom: '18px', fontSize: '13px', color: '#DC2626', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        <div style={fieldWrap}>
          <Label htmlFor="org-name">Name <span style={{ color: '#DC2626' }}>*</span></Label>
          <Input
            id="org-name"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Acme Corporation"
            required
            autoFocus
            style={{ marginTop: '6px' }}
          />
        </div>

        <div style={fieldWrap}>
          <Label htmlFor="org-domain">Domain</Label>
          <Input
            id="org-domain"
            value={domain}
            onChange={e => setDomain(e.target.value)}
            placeholder="acme.com"
            style={{ marginTop: '6px' }}
          />
          <div style={helpStyle}>Primary domain used for SSO and user matching (optional).</div>
        </div>

        <div style={fieldWrap}>
          <Label htmlFor="org-email">Contact Email</Label>
          <Input
            id="org-email"
            type="email"
            value={contactEmail}
            onChange={e => setContactEmail(e.target.value)}
            placeholder="admin@acme.com"
            style={{ marginTop: '6px' }}
          />
          <div style={helpStyle}>Primary billing / administrative contact (optional).</div>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
          <Button type="submit" disabled={!canSubmit}>
            {submitting ? 'Creating…' : 'Create Account'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/organizations')}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

export default OrganizationCreatePage;
